/**
 * Onboarding Phase 2 (2026-09-28) — the seen-stamp behind the one-time role
 * welcome sheet. Mirrors `src/utils/office/officeInboxSeen.js` (same
 * per-user, per-tenant localStorage read-receipt pattern + server
 * write-through), simplified for an even narrower job: this is a single ONE-
 * TIME ack ("has this person ever seen their role welcome"), not a rolling
 * high-water mark over a feed of rows — so there is no "unseen since"
 * selector here, only read/write/merge/collect.
 *
 * Everything here is PURE aside from localStorage/upsertNoticeReceipts calls
 * (all behind try/catch) so the seen-decision is unit-testable without a
 * database or a browser.
 *
 * RD-NOTICES-V2: write-throughs to the server as notice_key
 * 'role_welcome_seen' (acked_at). Registered in noticeReceiptAdapters.js so
 * `NoticeReceiptsSync` merges a server row into this store on hydration and
 * on every realtime receipt, and collects this store's local-only receipt
 * for the one-time post-hydration upload.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';

export const ROLE_WELCOME_NOTICE_KEY = 'role_welcome_seen';

export function ownsRoleWelcomeNoticeKey(noticeKey) {
  return noticeKey === ROLE_WELCOME_NOTICE_KEY;
}

/** Per USER *and* per TENANT — a shared office device must not cross-mark a
 *  second tenant's role welcome as seen (same reasoning as officeInboxSeen). */
export function roleWelcomeSeenKey(userId, tenantId) {
  return `cvx-role-welcome-seen-v1:${tenantId || 'tenant'}:${userId || 'anon'}`;
}

/** Fail-soft in both directions (private-mode `getItem` throws; a corrupted
 *  value must not crash a role home): unreadable → "never seen" (null). */
export function readRoleWelcomeSeenAt(userId, tenantId, storage) {
  try {
    const raw = (storage || window.localStorage).getItem(roleWelcomeSeenKey(userId, tenantId));
    if (!raw) return null;
    return Number.isNaN(Date.parse(raw)) ? null : raw;
  } catch {
    return null;
  }
}

/** MONOTONIC: writes max(current, new) — a device whose clock lags one that
 *  already synced a later ack must never move the watermark back. */
export function writeRoleWelcomeSeenAt(userId, tenantId, isoInstant, storage, opts = {}) {
  try {
    const next = maxIso(readRoleWelcomeSeenAt(userId, tenantId, storage), isoInstant);
    if (!next) return false;
    (storage || window.localStorage).setItem(roleWelcomeSeenKey(userId, tenantId), next);
    if (!opts.skipSync && userId && tenantId) {
      upsertNoticeReceipts({
        tenantId, userId, rows: [{ notice_key: ROLE_WELCOME_NOTICE_KEY, acked_at: next }],
      });
    }
    return true;
  } catch {
    return false;
  }
}

/** Server row → local store, MONOTONIC (never move the seen stamp back). */
export function mergeRoleWelcomeSeenFromServer(userId, tenantId, ackedAt, storage) {
  if (!userId || !tenantId || !ackedAt) return;
  const current = readRoleWelcomeSeenAt(userId, tenantId, storage);
  if (isNewerIso(ackedAt, current)) {
    writeRoleWelcomeSeenAt(userId, tenantId, ackedAt, storage, { skipSync: true });
  }
}

/** Snapshot for the one-time post-hydration upload. */
export function collectRoleWelcomeReceiptsForUpload(userId, tenantId, storage) {
  const seenAt = readRoleWelcomeSeenAt(userId, tenantId, storage);
  return seenAt ? [{ notice_key: ROLE_WELCOME_NOTICE_KEY, acked_at: seenAt }] : [];
}
